package org.zq.astrock_ai.network

import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import org.zq.astrock_ai.api.ChartPeriod
import org.zq.astrock_ai.api.StockCodes
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import org.zq.astrock_ai.api.dto.KlineBarDto
import org.zq.astrock_ai.api.dto.KlineResponseDto
import org.zq.astrock_ai.api.dto.LicenseStatusDto
import org.zq.astrock_ai.api.dto.QuantPlanResponseDto
import org.zq.astrock_ai.api.dto.QuantResultDto
import org.zq.astrock_ai.api.dto.QuantScanItemDto
import org.zq.astrock_ai.api.dto.QuantScanResponseDto
import org.zq.astrock_ai.api.dto.RecommendProgressDto
import org.zq.astrock_ai.api.dto.RecommendResponseDto
import org.zq.astrock_ai.api.dto.RecommendStockItemDto
import org.zq.astrock_ai.data.IndexQuote
import org.zq.astrock_ai.data.MockStockData
import org.zq.astrock_ai.data.StockProfile
import org.zq.astrock_ai.quant.QuantEngine
import org.zq.astrock_ai.quant.ZoneMarketContext
import org.zq.astrock_ai.quant.buildBuyZonePlan
import org.zq.astrock_ai.quant.buildSellZonePlan
import org.zq.astrock_ai.quant.toQuantBars
import org.zq.astrock_ai.ui.research.gushi.data.DeviceLicenseStore
import org.zq.astrock_ai.util.formatDecimal

data class BenchmarkSeries(
    val name: String,
    val bars: List<KlineBarDto>,
)

class MarketRepository(
    private val api: MarketApiClient = MarketApiClient(),
    private val eastMoney: EastMoneyDirectClient = EastMoneyDirectClient(),
    private val sina: SinaDirectClient = SinaDirectClient(),
) {
    private val benchmarkCache = mutableMapOf<String, BenchmarkSeries>()
    private val benchmarkLocks = mutableMapOf<String, Mutex>()
    private val marketContextClient by lazy { EastMoneyMarketContextClient() }
    private val newsClient by lazy { EastMoneyNewsClient() }

    var isLive: Boolean = false
        private set

    suspend fun loadTicker(): List<IndexQuote> = runCatching {
        val response = api.fetchTicker()
        isLive = true
        response.indices.map { it.toUi() }
    }.getOrElse {
        isLive = false
        MockStockData.indexQuotes
    }

    suspend fun loadMarketCenter(): MarketCenterData = runCatching {
        val data = api.fetchMarketCenter().toUi()
        isLive = true
        data
    }.getOrElse {
        isLive = false
        MarketCenterData(
            overview = MockStockData.marketOverview,
            indices = MockStockData.indexQuotes,
            sectors = MockStockData.sectorQuotes,
            gainers = MockStockData.gainers,
            losers = MockStockData.losers,
            turnoverLeaders = MockStockData.turnoverLeaders,
        )
    }

    suspend fun loadWatchlist(codes: List<String> = StockCodes.defaultWatchlist): List<StockProfile> {
        val ordered = codes.map { it.filter(Char::isDigit).takeLast(6) }.filter { it.length == 6 }.distinct()
        if (ordered.isEmpty()) return emptyList()

        val fromApi = runCatching {
            api.fetchWatchlist(ordered).map { it.toStockProfile() }
        }.getOrNull().orEmpty().associateBy { it.code }

        val missing = ordered.filter { it !in fromApi }
        val fromDirect = if (missing.isNotEmpty()) {
            runCatching { eastMoney.fetchQuotes(missing) }
                .getOrNull()
                .orEmpty()
                .map { it.toStockProfile() }
                .associateBy { it.code }
        } else {
            emptyMap()
        }

        val merged = ordered.mapNotNull { code -> fromApi[code] ?: fromDirect[code] }
        if (merged.isNotEmpty()) {
            isLive = true
            return merged
        }
        isLive = false
        return fallbackProfiles(ordered)
    }

    suspend fun loadKlineCloses(code: String, limit: Int = 90): List<Float> =
        loadKline(code, ChartPeriod.DAY, limit).bars.map { it.close }

    /**
     * 多源：后端 → 东财直连 → 新浪日K（仅日线）。
     */
    suspend fun loadKline(
        code: String,
        period: ChartPeriod = ChartPeriod.DAY,
        limit: Int = period.defaultLimit,
    ): KlineResponseDto {
        val fromApi = runCatching {
            api.fetchKline(code, limit, period.apiValue)
        }.getOrNull()

        if (fromApi != null && fromApi.bars.isNotEmpty()) {
            isLive = true
            return fromApi.copy(period = period.apiValue, chartType = period.chartType)
        }

        val direct = runCatching {
            eastMoney.fetchKline(code, period, limit)
        }.getOrNull()

        if (direct != null && direct.bars.isNotEmpty()) {
            isLive = true
            return direct
        }

        if (period == ChartPeriod.DAY) {
            val sinaK = runCatching { sina.fetchDailyKline(code, limit) }.getOrNull()
            if (sinaK != null && sinaK.bars.isNotEmpty()) {
                isLive = true
                return sinaK
            }
        }

        return KlineResponseDto(
            code = code,
            bars = emptyList(),
            period = period.apiValue,
            chartType = period.chartType,
        )
    }

    suspend fun loadKlineBars(
        code: String,
        limit: Int = 90,
        period: ChartPeriod = ChartPeriod.DAY,
    ): List<KlineBarDto> = loadKline(code, period, limit).bars

    /**
     * 优先云端量化接口；失败返回 null，由调用方本地 QuantEngine 兜底。
     */
    suspend fun loadQuant(
        code: String,
        strategyId: String = "dual_ma",
        lookbackDays: Int = 90,
        costPrice: Double? = null,
        quantity: Int? = null,
        params: Map<String, String> = emptyMap(),
    ): QuantPlanResponseDto? = runCatching {
        val response = api.fetchQuant(code, strategyId, lookbackDays, costPrice, quantity, params)
        if (response.quant.signal.isNotBlank()) {
            isLive = response.source == "eastmoney" || isLive
            response
        } else {
            null
        }
    }.getOrNull()

    suspend fun loadQuantResult(
        code: String,
        strategyId: String = "dual_ma",
        lookbackDays: Int = 90,
        costPrice: Double? = null,
        quantity: Int? = null,
        params: Map<String, String> = emptyMap(),
    ): QuantResultDto? = loadQuant(code, strategyId, lookbackDays, costPrice, quantity, params)?.quant

    /**
     * 自选股批量量化扫描。优先云端 /api/quant/scan；失败则本地逐票算。
     */
    suspend fun scanQuant(
        codes: List<String>,
        strategyId: String = "consensus",
        lookbackDays: Int = 90,
    ): QuantScanResponseDto {
        val normalized = codes
            .map { it.filter(Char::isDigit).takeLast(6) }
            .filter { it.length == 6 }
            .distinct()
            .take(30)
        if (normalized.isEmpty()) {
            return QuantScanResponseDto(strategyId = strategyId, count = 0)
        }
        val remote = runCatching {
            api.fetchQuantScan(normalized, strategyId, lookbackDays)
        }.getOrNull()
        if (remote != null && remote.items.isNotEmpty()) {
            isLive = remote.items.any { it.source == "eastmoney" } || isLive
            // 不伪造卖区：旧网关无 sellLow/sellHigh 时保持为空，UI 不展示
            return remote
        }
        return localQuantScan(normalized, strategyId, lookbackDays)
    }

    suspend fun fetchLicenseStatus(): LicenseStatusDto {
        val deviceId = DeviceLicenseStore.deviceId()
        val token = DeviceLicenseStore.load()?.token.orEmpty()
        return runCatching { api.fetchLicenseStatus(deviceId, token) }.getOrElse {
            LicenseStatusDto(ok = false, message = "无法校验开通状态，请稍后重试")
        }
    }

    /** 程序漏斗荐股（服务端）；并对每只补齐建议买入/卖出区间。 */
    suspend fun recommendTop(
        limit: Int = 5,
        boards: String = "cyb",
        style: String = org.zq.astrock_ai.api.RecommendStyle.OVERNIGHT,
    ): RecommendResponseDto {
        val remote = runCatching { api.fetchRecommend(limit, boards, style) }.getOrNull()
        if (remote != null) {
            isLive = remote.stocks.any { it.source == "eastmoney" } || isLive
            return enrichRecommendZones(remote)
        }
        return RecommendResponseDto(
            generatedAt = "",
            marketStatus = "UNAVAILABLE",
            marketRegime = "未知",
            riskLevel = "未知",
            recommendedPositionLimit = 0.0,
            candidatePoolSize = 0,
            scoredCount = 0,
            dataComplete = false,
            staleSources = listOf("api"),
            note = "荐股服务暂不可用，请确认网关 /api/quant/recommend",
            boards = org.zq.astrock_ai.api.RecommendBoards.parse(boards).toList(),
            boardsLabel = org.zq.astrock_ai.api.RecommendBoards.label(
                org.zq.astrock_ai.api.RecommendBoards.parse(boards),
            ),
            stocks = emptyList(),
        )
    }

    /**
     * 异步荐股：启动任务并轮询进度，[onProgress] 实时回调当前步骤。
     * 完成后返回名单（已补买卖区）。
     */
    suspend fun recommendTopWithProgress(
        limit: Int = 5,
        boards: String = "cyb",
        style: String = org.zq.astrock_ai.api.RecommendStyle.OVERNIGHT,
        onProgress: (RecommendProgressDto) -> Unit,
    ): RecommendResponseDto {
        val start = runCatching { api.startRecommendJob(limit, boards, style) }.getOrNull()
        if (start == null || start.jobId.isBlank()) {
            // 旧网关无 start 接口时回退同步
            onProgress(
                RecommendProgressDto(
                    stage = "同步扫描",
                    detail = "进度接口不可用，改用同步荐股…",
                    percent = 10,
                ),
            )
            return recommendTop(limit, boards, style).also {
                onProgress(
                    RecommendProgressDto(
                        stage = "完成",
                        detail = "共推荐 ${it.stocks.size} 只",
                        percent = 100,
                        done = true,
                        result = it,
                        steps = listOf("同步扫描", "完成"),
                    ),
                )
            }
        }
        onProgress(
            RecommendProgressDto(
                jobId = start.jobId,
                stage = "排队启动",
                detail = "任务 ${start.jobId}",
                percent = 2,
                steps = listOf("排队启动"),
            ),
        )
        var last: RecommendProgressDto? = null
        var finished = false
        var ticks = 0
        while (!finished && ticks < 900) { // 最多约 15 分钟
            ticks++
            kotlinx.coroutines.delay(1000)
            val snap = runCatching { api.fetchRecommendStatus(start.jobId) }.getOrNull() ?: continue
            last = snap
            onProgress(snap)
            if (!snap.done) continue
            finished = true
            val result = snap.result
            if (result != null) {
                isLive = result.stocks.any { it.source == "eastmoney" } || isLive
                return enrichRecommendZones(result)
            }
            if (snap.error.isNotBlank()) {
                return RecommendResponseDto(
                    marketStatus = "ERROR",
                    marketRegime = "失败",
                    riskLevel = "未知",
                    dataComplete = false,
                    staleSources = listOf("api"),
                    note = "荐股失败：${snap.error}",
                    boards = org.zq.astrock_ai.api.RecommendBoards.parse(boards).toList(),
                    boardsLabel = org.zq.astrock_ai.api.RecommendBoards.label(
                        org.zq.astrock_ai.api.RecommendBoards.parse(boards),
                    ),
                )
            }
        }
        return last?.result?.let { enrichRecommendZones(it) } ?: RecommendResponseDto(
            marketStatus = "TIMEOUT",
            note = "荐股超时或进度丢失，请重试",
            dataComplete = false,
            staleSources = listOf("api"),
            boards = org.zq.astrock_ai.api.RecommendBoards.parse(boards).toList(),
            boardsLabel = org.zq.astrock_ai.api.RecommendBoards.label(
                org.zq.astrock_ai.api.RecommendBoards.parse(boards),
            ),
        )
    }

    /** 网关未返回买卖区时，用本地日 K + 资金/公告补算。 */
    private suspend fun enrichRecommendZones(result: RecommendResponseDto): RecommendResponseDto {
        if (result.stocks.isEmpty()) return result
        val stocks = coroutineScope {
            result.stocks.map { item ->
                async { ensureRecommendZones(item) }
            }.awaitAll()
        }
        return result.copy(stocks = stocks)
    }

    private suspend fun ensureRecommendZones(item: RecommendStockItemDto): RecommendStockItemDto {
        val needBuy = item.buyLow <= 0 || item.buyHigh <= 0
        val needSell = item.sellLow <= 0 || item.sellHigh <= 0
        if (!needBuy && !needSell) return item
        return runCatching {
            val bars = loadKlineBars(item.code, limit = 120).toQuantBars()
            val px = item.price.takeIf { it > 0 } ?: bars.lastOrNull()?.close ?: 0.0
            if (px <= 0 || bars.size < 10) return@runCatching item
            val ctx = runCatching { marketContextClient.fetch(item.code) }.getOrNull()
            val headlines = runCatching {
                newsClient.fetchLatest(item.code).map { it.title }.take(16)
            }.getOrDefault(emptyList())
            val zoneMarket = ZoneMarketContext(
                todayMainNet = ctx?.todayMainNet,
                recentMainNet = ctx?.recentMainNet,
                headlines = headlines,
            )
            val signal = item.signal.ifBlank { "HOLD" }
            val buy = if (needBuy) {
                buildBuyZonePlan(bars, px, signal, market = zoneMarket)
            } else {
                null
            }
            val sell = if (needSell) {
                buildSellZonePlan(bars, px, signal, market = zoneMarket)
            } else {
                null
            }
            val buyOk = buy != null && buy.low > 0 && buy.high > 0
            val sellOk = sell != null && sell.low > 0 && sell.high > 0
            if (!buyOk && !sellOk) return@runCatching item
            val extraReasons = buildList {
                if (buyOk) {
                    add(
                        "建议买入 ${formatDecimal(buy!!.low)}–${formatDecimal(buy.high)}" +
                            if (buy.method.isNotBlank()) "（${buy.method}）" else "",
                    )
                }
                if (sellOk) {
                    add(
                        "建议卖出 ${formatDecimal(sell!!.low)}–${formatDecimal(sell.high)}" +
                            if (sell.method.isNotBlank()) "（${sell.method}）" else "",
                    )
                }
            }
            item.copy(
                buyLow = if (buyOk) buy!!.low else item.buyLow,
                buyHigh = if (buyOk) buy.high else item.buyHigh,
                buyMethod = if (buyOk) buy.method else item.buyMethod,
                buyConfidence = if (buyOk) buy.confidence else item.buyConfidence,
                buyReasons = if (buyOk) buy.reasons else item.buyReasons,
                buyHint = when {
                    !buyOk -> item.buyHint
                    item.buyHint.isNotBlank() -> item.buyHint
                    else ->
                        "${buy!!.method} ${formatDecimal(buy.low)}–${formatDecimal(buy.high)}，回踩可分批吸纳（研究参考）"
                },
                sellLow = if (sellOk) sell!!.low else item.sellLow,
                sellHigh = if (sellOk) sell.high else item.sellHigh,
                sellMethod = if (sellOk) sell.method else item.sellMethod,
                sellConfidence = if (sellOk) sell.confidence else item.sellConfidence,
                sellReasons = if (sellOk) sell.reasons else item.sellReasons,
                sellHint = when {
                    !sellOk -> item.sellHint
                    item.sellHint.isNotBlank() -> item.sellHint
                    else ->
                        "${sell!!.method} ${formatDecimal(sell.low)}–${formatDecimal(sell.high)}，触及可分批兑现（研究参考）"
                },
                reasons = (item.reasons + extraReasons).distinct(),
            )
        }.getOrDefault(item)
    }

    private suspend fun localQuantScan(
        codes: List<String>,
        strategyId: String,
        lookbackDays: Int,
    ): QuantScanResponseDto = coroutineScope {
        val items = codes.map { code ->
            async {
                val bars = loadKlineBars(code, limit = lookbackDays.coerceAtLeast(60)).toQuantBars()
                if (bars.size < 20) {
                    return@async QuantScanItemDto(
                        code = code,
                        signal = "HOLD",
                        strategyId = strategyId,
                        reason = "K线不足",
                        source = "local",
                    )
                }
                val plan = QuantEngine.buildPlan(bars, strategyId, lookbackDays = lookbackDays)
                val name = runCatching { loadWatchlist(listOf(code)).firstOrNull()?.name }.getOrNull().orEmpty()
                val exitAvg = plan.backtest.tradeDetails
                    .takeLast(8)
                    .map { it.exitPrice }
                    .takeIf { it.isNotEmpty() }
                    ?.average()
                val entryAvg = plan.backtest.tradeDetails
                    .takeLast(8)
                    .map { it.entryPrice }
                    .takeIf { it.isNotEmpty() }
                    ?.average()
                val ctx = runCatching { marketContextClient.fetch(code) }.getOrNull()
                val headlines = runCatching {
                    newsClient.fetchLatest(code).map { it.title }.take(16)
                }.getOrDefault(emptyList())
                val zoneMarket = ZoneMarketContext(
                    todayMainNet = ctx?.todayMainNet,
                    recentMainNet = ctx?.recentMainNet,
                    headlines = headlines,
                )
                val sell = org.zq.astrock_ai.quant.buildSellZonePlan(
                    bars = bars,
                    price = plan.signal.price,
                    signal = plan.signal.side,
                    backtestExitAvg = exitAvg,
                    market = zoneMarket,
                )
                val buy = org.zq.astrock_ai.quant.buildBuyZonePlan(
                    bars = bars,
                    price = plan.signal.price,
                    signal = plan.signal.side,
                    backtestEntryAvg = entryAvg,
                    market = zoneMarket,
                )
                QuantScanItemDto(
                    code = code,
                    name = name,
                    signal = plan.signal.side,
                    strategyId = plan.strategyId,
                    reason = plan.signal.reason,
                    price = plan.signal.price,
                    asOf = plan.signal.asOf,
                    buyVotes = plan.consensus?.buyCount ?: 0,
                    sellVotes = plan.consensus?.sellCount ?: 0,
                    source = "local",
                    sellLow = sell.low,
                    sellHigh = sell.high,
                    sellMethod = sell.method,
                    sellConfidence = sell.confidence,
                    sellReasons = sell.reasons,
                    buyLow = buy.low,
                    buyHigh = buy.high,
                    buyMethod = buy.method,
                    buyConfidence = buy.confidence,
                    buyReasons = buy.reasons,
                )
            }
        }.awaitAll().sortedWith(
            compareBy<QuantScanItemDto> {
                when (it.signal) {
                    "BUY" -> 0
                    "SELL" -> 1
                    else -> 2
                }
            }.thenBy { it.code },
        )
        QuantScanResponseDto(
            strategyId = strategyId,
            count = items.size,
            buyCount = items.count { it.signal == "BUY" },
            sellCount = items.count { it.signal == "SELL" },
            holdCount = items.count { it.signal == "HOLD" },
            items = items,
        )
    }

    suspend fun loadBenchmark(
        stockCode: String,
        limit: Int = 120,
        refresh: Boolean = false,
    ): BenchmarkSeries {
        val (secid, name) = benchmarkFor(stockCode)
        val lock = benchmarkLocks.getOrPut(secid) { Mutex() }
        return lock.withLock {
            if (!refresh) {
                benchmarkCache[secid]
                    ?.takeIf { it.bars.size >= limit }
                    ?.let { return@withLock it }
            }
            val bars = runCatching {
                eastMoney.fetchBenchmarkKline(secid, limit).bars
            }.getOrDefault(emptyList())
            val result = BenchmarkSeries(name, bars)
            if (bars.isNotEmpty()) benchmarkCache[secid] = result
            result
        }
    }

    suspend fun loadAddableStocks(existingCodes: Set<String>): List<StockProfile> = runCatching {
        val market = api.fetchMarketCenter()
        val fromRank = (market.gainers + market.turnoverLeaders)
            .distinctBy { it.code }
            .filter { it.code !in existingCodes }
            .mapNotNull { runCatching { api.fetchStock(it.code).toStockProfile() }.getOrNull() }
        fromRank.ifEmpty { MockStockData.addableStocks(existingCodes) }
    }.getOrElse {
        MockStockData.addableStocks(existingCodes)
    }

    private fun fallbackProfiles(codes: List<String>): List<StockProfile> =
        codes.mapNotNull { MockStockData.profileByCode(it) }

    private fun benchmarkFor(code: String): Pair<String, String> = when {
        code.startsWith("300") || code.startsWith("301") -> "0.399006" to "创业板指"
        code.startsWith("688") -> "1.000688" to "科创50"
        code.startsWith("6") -> "1.000001" to "上证指数"
        code.startsWith("8") || code.startsWith("4") -> "1.000300" to "沪深300"
        else -> "0.399001" to "深证成指"
    }
}
